import { apiGet, apiSend } from "../api/authDevApiClient.js";

/**
 * @typedef {object} LicenseActivationState
 * @property {boolean} activated
 * @property {boolean} needsActivation
 * @property {string} [machineId]
 * @property {string|null} [expiresAt]
 * @property {string|null} [licenseKeyMasked]
 * @property {string|null} [status]
 * @property {boolean} [enforcementDisabled]
 * @property {string} [message]
 */

/** In-flight dedupe — React StrictMode mounts effects twice in development. */
let licenseStatusInFlight = null;

/**
 * GET /api/license/status?deviceId=...
 * @returns {Promise<LicenseActivationState>}
 */
export async function fetchLicenseStatus() {
  if (licenseStatusInFlight) {
    return licenseStatusInFlight;
  }

  licenseStatusInFlight = (async () => {
    const body = await apiGet("/api/license/status");
    const data = body?.data ?? body;
    const activated = Boolean(data?.activated);
    return {
      activated,
      needsActivation: Boolean(
        data?.needsActivation !== undefined
          ? data.needsActivation
          : !activated,
      ),
      machineId: data?.machineId,
      expiresAt: data?.expiresAt ?? null,
      licenseKeyMasked: data?.licenseKeyMasked ?? null,
      status: data?.status ?? null,
      enforcementDisabled: Boolean(data?.enforcementDisabled),
    };
  })();

  try {
    return await licenseStatusInFlight;
  } finally {
    licenseStatusInFlight = null;
  }
}

/**
 * POST /api/license/activate
 * @param {{ licenseKey: string }} input
 * @returns {Promise<LicenseActivationState>}
 */
export async function activateLicense({ licenseKey }) {
  licenseStatusInFlight = null;
  const body = await apiSend("/api/license/activate", "POST", {
    licenseKey,
  });
  const data = body?.data ?? body;
  return {
    activated: Boolean(data?.activated ?? true),
    needsActivation: Boolean(data?.needsActivation ?? false),
    machineId: data?.machineId,
    expiresAt: data?.expiresAt ?? null,
    licenseKeyMasked: data?.licenseKeyMasked ?? null,
    status: data?.status ?? "valid",
    message: data?.message,
  };
}
